import React from 'react';

import FixedButton from '../FixedButton';
import classNames from 'classnames';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { GeneralModal } from 'ui/GeneralModal';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { SvgIcon } from 'ui/SvgIcon';
import CheckIcon from 'ui/Icons/tabler/check.component.svg';
import ExclamationTriangleIcon from 'ui/Icons/tabler/exclamation-triangle.component.svg';

export enum EConfirmationModalType {
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
}

interface IConfirmationModalProps {
  className?: string;
  type: EConfirmationModalType;
  isOpen?: boolean;
  title?: string;
  message?: React.ReactNode;
  extraMessage?: React.ReactNode;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  isConfirmLoading?: boolean;
  isCancelLoading?: boolean;
  isContentLoading?: boolean;
  width?: number;
  buttonsWrapperClassname?: string;
  renderAsContent?: boolean;
}

export const ConfirmationModal: React.FC<IConfirmationModalProps> = ({
  className,
  type,
  title,
  message,
  extraMessage,
  confirmButtonLabel,
  cancelButtonLabel,
  onConfirm,
  onCancel,
  isOpen = true,
  isConfirmLoading,
  isCancelLoading,
  isContentLoading,
  width = 500,
  buttonsWrapperClassname = '',
  renderAsContent = false,
}) => {
  const { isMobile } = useResponsive();
  if (!isOpen) {
    return null;
  }

  const borderColor = {
    warning: 'border-yellow-50',
    error: 'border-red-90',
    success: 'border-green-90',
  };

  const iconColor = {
    warning: 'fill-yellow-50',
    error: 'fill-red-90',
    success: 'stroke-green-90',
  };

  const confirmClassname = classNames('confirm', {
    'o:min-w-[32vw]': isMobile,
  });

  const cancelClassname = classNames('cancel', {
    'o:min-w-[32vw]': isMobile,
  });

  const buttonAlignment = confirmButtonLabel && cancelButtonLabel ? 'justify-between' : 'justify-center';

  if (isContentLoading) {
    if (renderAsContent) {
      return (
        <div className="flex items-center justify-center h-180px">
          <LoadingBar />
        </div>
      );
    }
    return (
      <GeneralModal
        modalOverlayClassName={classNames(className, 'items-start bg-black-true-transparent-60 z-11')}
        modalWindowClassName={`w-${width}px px-8 pb-10 pt-8 border-4 border-solid ${borderColor[type]} rounded-xl`}
        shouldCloseByClickingOutside
        isCloseButtonVisible={false}
      >
        <div className="flex items-center justify-center h-180px">
          <LoadingBar />
        </div>
      </GeneralModal>
    );
  }

  const content = (
    <>
      <div className="flex items-center">
        <div>
          {type === 'success' ? (
            <SvgIcon IconComponent={CheckIcon} className="w-12 h-12 fill-transparent stroke-green-90 stroke-2" />
          ) : (
            <SvgIcon IconComponent={ExclamationTriangleIcon} className={`w-12 h-12 ${iconColor[type]}`} />
          )}
        </div>
        <div className="ml-5">
          {title && <p className="title font-pt-sans font-bold text-black text-lg leading-lg m-0">{title}</p>}
        </div>
      </div>
      <div className="flex flex-col items-start pt-5 pb-8 gap-5">
        {message && <p className="message text-base text-black font-pt-sans leading-md m-0">{message}</p>}
        {extraMessage && <p className="message text-base text-black font-pt-sans leading-md m-0">{extraMessage}</p>}
      </div>

      <div className={`flex ${buttonAlignment} ${buttonsWrapperClassname}`}>
        {confirmButtonLabel && onConfirm && (
          <FixedButton
            className={confirmClassname}
            text={confirmButtonLabel}
            type="primary"
            onClick={onConfirm}
            isLoading={isConfirmLoading}
            disabled={isConfirmLoading || isCancelLoading}
          />
        )}
        {cancelButtonLabel && onCancel && (
          <FixedButton
            className={cancelClassname}
            text={cancelButtonLabel}
            type="secondary"
            onClick={onCancel}
            disabled={isConfirmLoading || isCancelLoading}
          />
        )}
      </div>
    </>
  );

  if (renderAsContent) {
    return content;
  }

  return (
    <GeneralModal
      modalOverlayClassName={classNames(className, 'items-start bg-black-true-transparent-60 z-11')}
      modalWindowClassName={`w-${width}px px-8 pb-10 pt-8 border-4 border-solid ${borderColor[type]} rounded-xl`}
      shouldCloseByClickingOutside
      isCloseButtonVisible={false}
    >
      {content}
    </GeneralModal>
  );
};

export default ConfirmationModal;
